import { useMemo } from "react";
import { SelectionBox } from "./SelectionBox";
import style from "./SelectionBoxWrapper.module.scss";
import { ReportConfigNodes, SimpleNodeFields } from "../../types/types";
import { getProbeName, getProductName } from "../../utils/utility";
import { useTranslation } from "react-i18next";

interface Props {
  nodes: Array<ReportConfigNodes>;
  handleNodeToggle: (id: number) => void;
  handleSensorToggle: (sens_type: string) => void;
}

export function SelectionBoxWrapper({
  nodes,
  handleNodeToggle,
  handleSensorToggle,
}: Props) {
  const { t } = useTranslation();

  // Separate nodes into regular and hidden based on removed_time
  const { regularNodes, hiddenNodes } = useMemo(() => {
    const regular: ReportConfigNodes[] = [];
    const hidden: ReportConfigNodes[] = [];

    nodes.forEach(node => {
      if (node.removed_time) {
        hidden.push(node);
      } else {
        regular.push(node);
      }
    });

    return { regularNodes: regular, hiddenNodes: hidden };
  }, [nodes]);

  const availableSensors = useMemo(() => {
    // Get all node fields(sensors) from selected nodes into single array
    // Include both regular nodes and hidden nodes that are selected
    const sensors = [
      ...regularNodes.filter((node) => node.selected),
      ...hiddenNodes.filter((node) => node.selected)
    ].flatMap((node) => node.fields);

    // save as a record for convinience
    const sensorMap: Record<string, SimpleNodeFields> = {};

    for (const sensor of sensors) {
      const type = sensor.sens_type;
      // Remove duplicates and make sure that when one of the duplicates is
      // enabled we preserve that in the resulting array
      if (!sensorMap[type] || sensor.enabled) {
        sensorMap[type] = sensor;
      }
    }
    // Convert the sensorMap back to an object array
    return Object.values(sensorMap).map(field => ({
      sens_type: field.sens_type,
      enabled: field.enabled
    }));
  }, [regularNodes, hiddenNodes]);

  return <div className={style.selectionBoxContainer}>
    <SelectionBox
      items={regularNodes}
      hiddenItems={hiddenNodes}
      getLabel={(item) => (
        <div style={{ padding: "0.4rem", lineHeight: "1.6" }}>
          {item.sensor_name}
          <br />
          {getProductName(item.node_type, t)}
          {item.probe_type && ` (${getProbeName(item.probe_type, t)})`}
        </div>
      )}
      checked={(item) => item.selected}
      onClick={(item) => handleNodeToggle(item.id)}
      itemKey={(item) => item.id}
    />
    <SelectionBox
      items={availableSensors}
      getLabel={(item) => <>{item.sens_type}</>}
      checked={(item) => item.enabled}
      onClick={(item) => handleSensorToggle(item.sens_type)}
      itemKey={(item) => item.sens_type}
    />
  </div>
}